/**
 * useCompanionLesson — fetches dynamic lesson content from the LMS Google Sheet
 * via the public API at learn-music.nathanielschool.com.
 *
 * The list is fetched once per app session (module-level cache) and keyed by
 * slug ("the-beatles-1", "piano-foundation-5", etc.).  Each LessonShell calls
 * this hook; if the fetch is already in-flight or complete the cached result
 * is returned immediately on the second call.
 *
 * Failure mode: silently returns null — the lesson renders normally with its
 * hardcoded TSX content and no CompanionDynamic section appears.
 */

import { useState, useEffect } from "react";

const LMS_API =
  "https://learn-music.nathanielschool.com/api/companion/lessons";

export interface TheoryBox {
  label: string;
  contentHtml: string;
}

export interface RomanNumeralRow {
  chord: string;
  rn: string;
  function: string; // "Tonic" | "Subdominant" | "Dominant" | ...
}

export interface CompanionLesson {
  lessonSlug: string;
  proseHtml: string;
  theoryBoxesJson: TheoryBox[];
  romanNumeralsJson: RomanNumeralRow[];
  notationImagesJson: string[];
  midiUrl: string;
  // Optional lesson meta — present only for self-serve lessons created via the
  // LMS Admin → Companion editor (a brand-new lesson with no hand-built TSX
  // component). Existing lessons leave these blank and use their baked data.
  videoUrl?: string;
  title?: string;
  description?: string;
  thumbUrl?: string;
}

// Module-level cache — only one network request per browser session
let _cache: Record<string, CompanionLesson> | null = null;
let _fetchPromise: Promise<Record<string, CompanionLesson>> | null = null;

function fetchAll(): Promise<Record<string, CompanionLesson>> {
  if (_cache) return Promise.resolve(_cache);
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = fetch(LMS_API, { credentials: "omit" })
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((data: { lessons?: CompanionLesson[] }) => {
      const map: Record<string, CompanionLesson> = {};
      for (const raw of data?.lessons ?? []) {
        const r = raw as Partial<CompanionLesson> & { lessonSlug?: string };
        if (!r.lessonSlug) continue;
        // Normalise at the boundary: half-filled rows can arrive with fields
        // missing or null, and downstream render code assumes strings/arrays
        // (.trim(), .length). Coerce once here so no component can crash.
        map[r.lessonSlug] = {
          lessonSlug: r.lessonSlug,
          proseHtml: typeof r.proseHtml === "string" ? r.proseHtml : "",
          theoryBoxesJson: Array.isArray(r.theoryBoxesJson) ? r.theoryBoxesJson : [],
          romanNumeralsJson: Array.isArray(r.romanNumeralsJson) ? r.romanNumeralsJson : [],
          notationImagesJson: Array.isArray(r.notationImagesJson)
            ? r.notationImagesJson.filter((x): x is string => typeof x === "string")
            : [],
          midiUrl: typeof r.midiUrl === "string" ? r.midiUrl : "",
          videoUrl: typeof r.videoUrl === "string" ? r.videoUrl : undefined,
          title: typeof r.title === "string" ? r.title : undefined,
          description: typeof r.description === "string" ? r.description : undefined,
          thumbUrl: typeof r.thumbUrl === "string" ? r.thumbUrl : undefined,
        };
      }
      _cache = map;
      _fetchPromise = null;
      return map;
    })
    .catch((err) => {
      console.warn("[CompanionData] fetch failed — dynamic content disabled:", err);
      _fetchPromise = null; // allow retry on next mount
      return {};
    });

  return _fetchPromise;
}

/**
 * Returns the dynamic companion data for one lesson, or null while loading /
 * on error / if no admin content has been saved for this lesson yet.
 *
 * @param courseSlug  e.g. "the-beatles"
 * @param lessonOrder e.g. 1 or "1"
 */
export function useCompanionLesson(
  courseSlug: string,
  lessonOrder: string | number,
): CompanionLesson | null {
  const slug = `${courseSlug}-${String(lessonOrder)}`;
  const [lesson, setLesson] = useState<CompanionLesson | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAll().then((map) => {
      if (!cancelled) setLesson(map[slug] ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return lesson;
}
